import { useEffect, useState } from 'react';

const TASKBAR_MOBILE = 64;  // h-16
const TASKBAR_DESKTOP = 80; // sm:h-20
const MOBILE_BREAKPOINT = 640;
const BOTTOM_CLEARANCE = 16;

/** Conservative icon cell size (icon + label + padding) so rows never spill into the taskbar. */
function slotMetrics(viewportWidth: number) {
  if (viewportWidth >= 768) return { height: 140, width: 100, gap: 12 };
  if (viewportWidth < MOBILE_BREAKPOINT) return { height: 108, width: 76, gap: 4 };
  return { height: 120, width: 88, gap: 8 };
}

function taskbarHeight(viewportWidth: number) {
  return viewportWidth < MOBILE_BREAKPOINT ? TASKBAR_MOBILE : TASKBAR_DESKTOP;
}

function computeLayout(iconCount: number) {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const topPad = width < MOBILE_BREAKPOINT ? 12 : 16;
  const sidePad = width < MOBILE_BREAKPOINT ? 8 : 16;
  const taskbar = taskbarHeight(width);
  const slot = slotMetrics(width);

  const availableHeight = Math.max(0, height - taskbar - topPad - BOTTOM_CLEARANCE);
  const availableWidth = Math.max(0, width - sidePad);
  const iconsPerColumn = Math.max(
    1,
    Math.floor((availableHeight + slot.gap) / (slot.height + slot.gap)),
  );

  return {
    availableWidth,
    topPadding: topPad,
    taskbarHeight: taskbar,
    bottomClearance: BOTTOM_CLEARANCE,
    iconsPerColumn,
    gap: slot.gap,
    columnWidth: slot.width,
    iconCount,
  };
}

/** Desktop icon column layout — recalculates on browser tab resize. */
export function useDesktopIconLayout(iconCount: number) {
  const [layout, setLayout] = useState(() => computeLayout(iconCount));

  useEffect(() => {
    let raf = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setLayout(computeLayout(iconCount)));
    };

    update();
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, [iconCount]);

  return layout;
}
